import { useIsDesktop } from '@/_hooks/useBreakpoint';

import React, { SetStateAction, useState } from 'react';
import LoginDesktop from './login.desktop';
import LoginMobile from './login.mobile';
import { useSelector } from 'react-redux';
import { IRedux, IUser } from '@/_store/types';
import customerService from '@/_service/customer.service';
import { toaster } from '@/components/ui/toaster';
import { getSource } from '@/_utils/platform';
import { isPhoneValid } from '../../../PhoneInput/isPhoneValid';
import GoogleBtn from '../Google';
import { useTelegram } from '@/_providers/telegram';
import { FEATURED_BRANDS } from '@/_utils/Constants';

export interface ILoginProps {
  values: { phone: string; name: string };
  onChange: (name: string, value: string) => void;
  onSuccess: (data: IUser) => void;
  setActiveWindow: React.Dispatch<SetStateAction<string>>;
}

const Login = ({
  values,
  onChange,
  onSuccess,
  setActiveWindow,
}: ILoginProps) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const isDesktop = useIsDesktop();

  const { tg_chat_id } = useTelegram();
  const settings = useSelector((state: IRedux) => state.settings);
  const { fcm_token, platform_id, is_hall } = useSelector(
    (state: IRedux) => state.session,
  );

  const onSendOTP = async (
    data: { phone: string; name: string; registration_source?: string },
    type: 'login-confirmation' | 'register-confirmation',
  ) => {
    try {
      if (type === 'login-confirmation') {
        await customerService.login(
          {
            phone: data?.phone,
            fcm_token: settings.fcm_token || undefined,
          },
          FEATURED_BRANDS.yaponamama,
        );
        setActiveWindow('login-confirmation');
      } else if (type === 'register-confirmation') {
        setActiveWindow('register');
      }
    } catch (error: any) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const loginWithoutOTP = async (data: {
    phone: string;
    name: string;
    registration_source?: string;
  }) => {
    try {
      const response = await customerService.loginWithoutOTP(
        {
          phone: data?.phone,
          ...(tg_chat_id ? { tg_chat_id } : {}),
          ...(fcm_token ? { fcm_token } : {}),
        },
        FEATURED_BRANDS.yaponamama,
        platform_id,
      );
      onSuccess(response?.data);
    } catch (error: any) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async (e: any, onError: () => void) => {
    e.preventDefault();

    if (error) setError(false);

    setLoading(true);

    const isValid = await isPhoneValid(values?.phone);

    if (isValid) {
      const source = getSource({ is_hall });

      if ((source === 'ios' || source === 'android') && !settings.fcm_token) {
        toaster.create({
          title: 'FCM token is not found!',
          type: 'error',
        });
        return;
      }

      customerService
        .getUserByPhone({ phone: values?.phone }, FEATURED_BRANDS.yaponamama)
        .then(() => {
          if (!settings?.data?.data?.is_otp) {
            loginWithoutOTP(values);
          } else if (settings?.data?.data?.is_otp) {
            onSendOTP(values, 'login-confirmation');
          }
        })
        .catch(() => {
          setActiveWindow('register');
        });
    } else {
      onError();
      setLoading(false);
    }
  };

  return (
    <>
      {isDesktop ? (
        <LoginDesktop
          error={error}
          values={values}
          loading={loading}
          onLogin={onLogin}
          onChange={onChange}
        >
          <GoogleBtn onSuccess={onSuccess} />
        </LoginDesktop>
      ) : (
        <LoginMobile
          error={error}
          values={values}
          loading={loading}
          onLogin={onLogin}
          onChange={onChange}
        >
          <GoogleBtn onSuccess={onSuccess} />
        </LoginMobile>
      )}
    </>
  );
};

export default Login;
